import { lazy, Suspense } from 'react';
import { createBrowserRouter, createRoutesFromElements, redirect, Route } from 'react-router';
import { RootErrorBoundary } from '../components/RootErrorBoundary';
const Game = lazy(() => import('../features/game/Game'));
import { profileLoader } from '../features/profile/loader';
const Home = lazy(() => import('../features/home/HomePage'));
import { SearchPage } from '../features/search/SearchPage';
const PlatformerListPage = lazy(() =>
    import('../features/platformer-list/PlatformerListPage').then((module) => ({ default: module.PlatformerListPage })),
);
const ReferencesPage = lazy(() =>
    import('../features/references/ReferencesPage').then((module) => ({ default: module.ReferencesPage })),
);
const PacksPage = lazy(() => import('../features/packs/PacksPage'));
import { About } from '../features/about/AboutPage';
const CrossroadPackPage = lazy(() =>
    import('../features/packs/crossroads/CrossroadPackPage').then((module) => ({ default: module.CrossroadsPackPage })),
);
const PackPage = lazy(() => import('../features/pack/PackPage').then((module) => ({ default: module.PackPage })));
const StaffPage = lazy(() => import('../features/staff/StaffPage').then((module) => ({ default: module.StaffPage })));
import { LevelPage } from '../features/level/LevelPage';
import { LoginPage } from '../features/login/LoginPage';
import { SignUpPage } from '../features/signup/SignUpPage';
const Profile = lazy(() =>
    import('../features/profile/ProfilePage').then((module) => ({ default: module.ProfilePage })),
);
const NotificationsPage = lazy(() =>
    import('../features/notifications/NotificationsPage').then((module) => ({ default: module.NotificationsPage })),
);
const Settings = lazy(() =>
    import('../features/settings/SettingsPage').then((module) => ({ default: module.SettingsPage })),
);
const AccountSettings = lazy(() => import('../features/settings/account/Account'));
const ClientSiteSettings = lazy(() => import('../features/settings/site/Site'));
const ProfileSettings = lazy(() => import('../features/settings/profile/Profile'));
const SubmissionSettings = lazy(() => import('../features/settings/submissions/SubmissionSettings'));
const Appearance = lazy(() => import('../features/settings/appearance/Appearance'));
const Developer = lazy(() => import('../features/settings/developer/Developer'));
const ForgotPasswordPage = lazy(() =>
    import('../features/forgot-password/ForgotPasswordPage').then((module) => ({ default: module.ForgotPasswordPage })),
);
const ListPage = lazy(() => import('../features/list/ListPage').then((module) => ({ default: module.ListPage })));
const GeneratorsPage = lazy(() =>
    import('../features/generators/GeneratorsPage').then((module) => ({ default: module.GeneratorsPage })),
);
const Alphabet = lazy(() => import('../features/generators/alphabet/Alphabet'));
const Roulette = lazy(() => import('../features/generators/roulette/Roulette'));
const ReverseRoulette = lazy(() => import('../features/generators/reverseRoulette/ReverseRoulette'));
const TierRoulette = lazy(() => import('../features/generators/tierRoulette/TierRoulette'));
const Decathlon = lazy(() => import('../features/generators/decathlon/Decathlon'));
const ChangelogsPage = lazy(() =>
    import('../features/changelogs/ChangelogsPage').then((module) => ({ default: module.ChangelogsPage })),
);
import FloatingLoadingSpinner from '../components/ui/FloatingLoadingSpinner';
import MainLayout from '../layouts/MainLayout';
import { listLoader } from '../features/list/list.loader';
import type { QueryClient } from '@tanstack/react-query';
import { levelLoader } from '../features/level/level.loader';
import { searchLoader } from '../features/search/search.loader';
import OAuth2Layout from '../layouts/oauth2/OAuth2';
import { AuthorizePage } from '../features/oauth2/authorize/AuthorizePage';
import { AuthorizeLoader } from '../features/oauth2/authorize/authorize.loader';
import { InvalidPage } from '../features/oauth2/invalid/InvalidPage';
const ApplicationsPage = lazy(() =>
    import('../features/applications/ApplicationsPage').then((module) => ({ default: module.ApplicationsPage })),
);
const Application = lazy(() => import('../features/applications/application/application.page'));
import { applicationLoader } from '../features/applications/application/application.loader';
import { TotpPage } from '../features/totp/TotpPage';
import { TotpRegisterPage } from '../features/totp/TotpRegisterPage';
import { totpRegisterLoader } from '../features/totp/totp-register.loader';
import { SubmitPage } from '../features/submit/SubmitPage';
import { routes } from './route-definitions';
import { submitLoader } from '../features/submit/submit.loader';
import { Connections } from '../features/settings/index';
import { SessionsPage } from '../features/settings/sessions/sessions.page';
import { staffRoutes } from '../features/admin/routes';
const SparkyPackPage = lazy(async () => ({
    default: (await import('../features/packs/sparky/SparkyPackPage')).SparkyPackPage,
}));
const GddlpPage = lazy(async () => ({
    default: (await import('../features/packs/gddlp/GddlpPage')).GddlpPage,
}));

export const router = (queryClient: QueryClient) =>
    createBrowserRouter(
        createRoutesFromElements([
            <Route element={<OAuth2Layout />} path='/oauth2'>
                <Route element={<AuthorizePage />} path='authorize' loader={AuthorizeLoader} />
                <Route element={<InvalidPage />} path='invalid' />
            </Route>,
            <Route element={<MainLayout />} ErrorBoundary={RootErrorBoundary}>
                <Route path='/' element={<Home />} />
                <Route path={routes.developer.applications.path} element={<ApplicationsPage />} />
                <Route
                    path='developer/applications/:appID'
                    element={<Application />}
                    loader={applicationLoader(queryClient)}
                />
                <Route path='search' element={<SearchPage />} loader={searchLoader} />
                <Route path={routes.submit.path} element={<SubmitPage />} />
                <Route path={routes.submit.level.path} element={<SubmitPage />} loader={submitLoader(queryClient)} />
                <Route path={routes.platformerList.path} element={<PlatformerListPage />} />
                <Route path='references' element={<ReferencesPage />} />
                <Route path='packs' element={<PacksPage />} />
                <Route path='about' element={<About />} />
                <Route path='pack/crossroads' element={<CrossroadPackPage />} />
                <Route path='pack/gddlp' element={<GddlpPage />} />
                <Route path='pack/sparky' element={<SparkyPackPage />} />
                <Route path='pack/:packID' element={<PackPage />} />
                <Route path='staff' element={<StaffPage />} />
                <Route path={routes.auth.totp.path} element={<TotpPage />} />
                <Route path='auth/totp/register' element={<TotpRegisterPage />} loader={totpRegisterLoader} />
                <Route path='level/:levelID' element={<LevelPage />} loader={levelLoader(queryClient)} />
                <Route path='login' element={<LoginPage />} />
                <Route path='signup' element={<SignUpPage />} />
                <Route path='profile' loader={profileLoader(queryClient)} />
                <Route path='profile/:userID' element={<Profile />} loader={profileLoader(queryClient)} />
                <Route path='notifications' element={<NotificationsPage />} />
                <Route path='game' element={<Game />} />
                <Route path='settings' element={<Settings />}>
                    <Route path='account' element={<AccountSettings />} />
                    <Route path='appearance' element={<Appearance />} />
                    <Route path='connections' element={<Connections />} />
                    <Route path='developer' element={<Developer />} />
                    <Route path='profile' element={<ProfileSettings />} />
                    <Route path='sessions' element={<SessionsPage />} />
                    <Route path='site' element={<ClientSiteSettings />} />
                    <Route path='submission' element={<SubmissionSettings />} />
                </Route>
                <Route path='list' loader={() => redirect('/search')} element={<p>hi</p>} />
                <Route path='list/:listID' element={<ListPage />} loader={listLoader(queryClient)} />
                <Route path={routes.forgotPassword.path} element={<ForgotPasswordPage />} />
                <Route
                    path='generators'
                    element={
                        <Suspense fallback={<FloatingLoadingSpinner />}>
                            <GeneratorsPage />
                        </Suspense>
                    }
                >
                    <Route path='alphabet' element={<Alphabet />} />
                    <Route path='roulette' element={<Roulette />} />
                    <Route path={routes.generators.reverseRoulette.path} element={<ReverseRoulette />} />
                    <Route path={routes.generators.tierRoulette.path} element={<TierRoulette />} />
                    <Route path='decathlon' element={<Decathlon />} />
                </Route>
                <Route path={routes.changelogs.path} element={<ChangelogsPage />} />
            </Route>,
            staffRoutes(queryClient),
        ]),
    );
